import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateElementPosition, updateElementProps } from "@/store/slideEditorSlice";
import type { ShapeElement } from "@ai-teach/types";
import { useEffect, useRef, useState } from "react";

interface ShapeElementToolbarProps {
  shapeElement: ShapeElement;
}

export function ShapeElementToolbar({ shapeElement }: ShapeElementToolbarProps) {
  const dispatch = useAppDispatch();
  const { slide, selectedId } = useAppSelector((state) => state.slideEditor.present);
  const [localFill, setLocalFill] = useState(shapeElement?.fill || "#000000");
  const fillTimer = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    setLocalFill(shapeElement?.fill || "#000000");
  }, [shapeElement?.fill]);

  if (!slide) return null;

  const selectedElement = slide.elements.find((el) => el.id === selectedId);

  const handleFillColorChange = (color: string) => {
    setLocalFill(color);
    if (fillTimer.current) clearTimeout(fillTimer.current);
    fillTimer.current = setTimeout(() => {
      if (selectedId) {
        dispatch(updateElementProps({ id: selectedId, props: { fill: color } }));
      }
    }, 80);
  };

  const handlePositionChange = (axis: "x" | "y", value: number) => {
    if (selectedId && selectedElement) {
      const newPosition = {
        x: axis === "x" ? value : selectedElement.position.x,
        y: axis === "y" ? value : selectedElement.position.y,
      };
      dispatch(updateElementPosition({ id: selectedId, ...newPosition }));
    }
  };

  return (
    <>
      {/* Fill Color Picker */}
      <div className="flex items-center gap-1 relative group">
        <label htmlFor="shape-color" className="cursor-pointer">
          <div className="relative">
            <div
              className="w-8 h-8 rounded-lg border-2 border-gray-300 transition-all duration-200 group-hover:border-primary-400 group-hover:scale-105 group-hover:shadow-md"
              style={{ backgroundColor: localFill }}
            />
            <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-sm">
              <div className="w-3 h-3 rounded-full bg-gradient-to-br from-red-400 via-yellow-400 to-blue-400" />
            </div>
          </div>
        </label>
        <input
          id="shape-color"
          type="color"
          value={localFill}
          onChange={(e) => handleFillColorChange(e.target.value)}
          className="absolute opacity-0 w-0 h-0 pointer-events-none"
          title="Fill Color"
        />
      </div>

      <div className="w-px h-6 bg-gray-300" />

      {/* Position Controls */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          <label htmlFor="pos-x" className="text-xs font-semibold text-gray-500 uppercase">
            X
          </label>
          <input
            id="pos-x"
            type="number"
            value={Math.round(selectedElement?.position.x || 0)}
            onChange={(e) => handlePositionChange("x", Number(e.target.value))}
            className="w-16 text-gray-700 px-2 py-1.5 border border-gray-300 rounded-lg text-xs font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-center bg-gray-50/50 transition-all"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <label htmlFor="pos-y" className="text-xs font-semibold text-gray-500 uppercase">
            Y
          </label>
          <input
            id="pos-y"
            type="number"
            value={Math.round(selectedElement?.position.y || 0)}
            onChange={(e) => handlePositionChange("y", Number(e.target.value))}
            className="w-16 text-gray-700 px-2 py-1.5 border border-gray-300 rounded-lg text-xs font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-center bg-gray-50/50 transition-all"
          />
        </div>
      </div>
    </>
  );
}
